import { createFileRoute, Link } from "@tanstack/react-router";
import { GUIDES } from "@/lib/guides-data";

export const Route = createFileRoute("/guide-audit")({
  head: () => ({
    meta: [
      { title: "Guide Dedup Audit — Pal Stars" },
      {
        name: "description",
        content:
          "Internal duplicate-content audit for every Pal Stars Palworld guide: repeated slugs, titles, article headings and intro copy.",
      },
      { property: "og:title", content: "Guide Dedup Audit — Pal Stars" },
      { property: "og:description", content: "Duplicate-content audit across every Pal Stars Palworld guide and article." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/guide-audit" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, follow" },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/guide-audit" }],
  }),
  component: GuideAuditPage,
});

type Row = { key: string; where: string[] };

function norm(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9 ]+/g, " ").replace(/\s+/g, " ").trim();
}

function dupes(entries: { key: string; where: string }[]): Row[] {
  const map = new Map<string, string[]>();
  for (const e of entries) {
    if (!e.key) continue;
    const list = map.get(e.key) ?? [];
    list.push(e.where);
    map.set(e.key, list);
  }
  return [...map.entries()]
    .filter(([, where]) => where.length > 1)
    .map(([key, where]) => ({ key, where }))
    .sort((a, b) => b.where.length - a.where.length);
}

function GuideAuditPage() {
  const guideSlugs = dupes(GUIDES.map((g) => ({ key: g.slug, where: g.title })));
  const guideTitles = dupes(GUIDES.map((g) => ({ key: norm(g.title), where: `/guides/${g.slug}` })));

  const articleSlugs = dupes(
    GUIDES.flatMap((g) => g.articles.map((a) => ({ key: `${g.slug}/${a.slug}`, where: a.title }))),
  );
  const articleTitles = dupes(
    GUIDES.flatMap((g) => g.articles.map((a) => ({ key: norm(a.title), where: `/guides/${g.slug}/${a.slug}` }))),
  );
  const articleIntros = dupes(
    GUIDES.flatMap((g) =>
      g.articles.map((a) => ({ key: norm(a.intro).slice(0, 120), where: `/guides/${g.slug}/${a.slug}` })),
    ),
  );
  const badCounts = GUIDES.filter((g) => g.articles.length !== 5);

  const groups: { name: string; note: string; rows: Row[] }[] = [
    { name: "Duplicate guide slugs", note: "Two guides resolving to the same /guides/<slug> URL.", rows: guideSlugs },
    { name: "Duplicate guide titles", note: "Same title text on more than one guide hub.", rows: guideTitles },
    { name: "Duplicate article URLs", note: "Same guide + article slug pair used twice.", rows: articleSlugs },
    { name: "Duplicate article titles", note: "Same headline across articles — a duplicate-title signal for Google.", rows: articleTitles },
    { name: "Duplicate article intros", note: "First 120 normalised characters of the intro repeated.", rows: articleIntros },
  ];

  const total = groups.reduce((n, g) => n + g.rows.length, 0) + badCounts.length;
  const articleCount = GUIDES.reduce((n, g) => n + g.articles.length, 0);

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10">
      <h1 className="text-3xl sm:text-4xl font-bold">Guide Dedup Audit</h1>
      <p className="mt-2 max-w-2xl text-muted-foreground">
        Scans all {GUIDES.length} guides and {articleCount} articles for duplicate URLs, titles and intro copy so no two
        pages compete for the same query.
      </p>

      <div className="mt-4 flex flex-wrap gap-3 text-xs">
        <span className={`rounded-full px-3 py-1 ${total ? "bg-rose-500/15 text-rose-400" : "bg-emerald-500/15 text-emerald-400"}`}>
          {total ? `${total} duplicate group${total === 1 ? "" : "s"}` : "No duplicates found"}
        </span>
        <Link to="/data-integrity" className="rounded-full bg-muted px-3 py-1 text-primary hover:underline">
          Data integrity report →
        </Link>
        <Link to="/link-audit" className="rounded-full bg-muted px-3 py-1 text-primary hover:underline">
          Live link audit →
        </Link>
      </div>

      <div className="mt-6 space-y-4">
        {groups.map((g) => (
          <section key={g.name} className="glass rounded-xl p-4">
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-semibold">{g.name}</h2>
              <span className={g.rows.length ? "text-rose-400 text-sm" : "text-emerald-400 text-sm"}>
                {g.rows.length ? `${g.rows.length} FOUND` : "PASS"}
              </span>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{g.note}</p>
            {g.rows.length > 0 && (
              <ul className="mt-2 space-y-1 text-sm">
                {g.rows.slice(0, 40).map((r) => (
                  <li key={r.key} className="text-rose-400">
                    <span className="font-mono text-xs">{r.key}</span>{" "}
                    <span className="text-muted-foreground">— {r.where.join(" · ")}</span>
                  </li>
                ))}
                {g.rows.length > 40 && (
                  <li className="text-muted-foreground">…and {g.rows.length - 40} more</li>
                )}
              </ul>
            )}
          </section>
        ))}

        <section className="glass rounded-xl p-4">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-semibold">Article count per guide (expects 5)</h2>
            <span className={badCounts.length ? "text-amber-400 text-sm" : "text-emerald-400 text-sm"}>
              {badCounts.length ? `${badCounts.length} OFF-TARGET` : "PASS"}
            </span>
          </div>
          {badCounts.length > 0 && (
            <ul className="mt-2 space-y-1 text-sm text-amber-400">
              {badCounts.map((g) => (
                <li key={g.slug}>
                  <span className="font-mono text-xs">/guides/{g.slug}</span> — {g.articles.length} articles
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
